import React from 'react';
import { Clock, Mail, MapPin, Phone } from 'lucide-react';
import { Location } from '../../types';
import { BloomBadge } from '../UI/Bloom';
import { EmptyState } from '../UI/Bloom/flow';
import { T, rgba } from '../../utils/adminTheme';

interface LocationSelectionProps {
  locations: Location[];
  selectedLocation: Location | null;
  onLocationSelect: (location: Location) => void;
}

export const LocationSelection: React.FC<LocationSelectionProps> = ({
  locations,
  selectedLocation,
  onLocationSelect
}) => {
  if (locations.length === 0) {
    return (
      <EmptyState
        title="Keine Standorte verfügbar"
        hint="Bitte Standortdaten im Admin-Bereich prüfen."
      />
    );
  }

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
      gap: 14,
    }}>
      {locations.map(location => {
        const active = selectedLocation?.id === location.id;

        return (
          <button
            key={location.id}
            onClick={() => onLocationSelect(location)}
            style={{
              textAlign: 'left',
              background: active ? T.surface2 : T.surface,
              border: `1px solid ${active ? T.accent : T.line}`,
              borderRadius: T.r.lg,
              padding: 20,
              cursor: 'pointer',
              color: T.text,
              boxShadow: active ? `0 14px 34px ${rgba(T.accent, 0.16)}` : 'none',
              display: 'grid',
              gap: 16,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
              <div>
                <h2 style={{
                  margin: 0,
                  fontFamily: T.display,
                  fontStyle: 'italic',
                  fontSize: 27,
                  fontWeight: 500,
                  lineHeight: 1.05,
                }}>
                  {location.name}
                </h2>
                {location.address && (
                  <div style={{ marginTop: 6, color: T.muted, fontFamily: T.body, fontSize: 13 }}>
                    {location.address}
                  </div>
                )}
              </div>
              <BloomBadge fg={location.active ? T.ok : T.danger} bg={rgba(location.active ? T.ok : T.danger, 0.13)} dot>
                {location.active ? 'Geöffnet' : 'Geschlossen'}
              </BloomBadge>
            </div>

            <div style={{ display: 'grid', gap: 9, color: T.muted, fontFamily: T.body, fontSize: 13 }}>
              {location.phone && (
                <span style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                  <Phone size={15} />
                  {location.phone}
                </span>
              )}
              {location.email && (
                <span style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                  <Mail size={15} />
                  {location.email}
                </span>
              )}
              <span style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                <Clock size={15} />
                {location.openTimes?.start ?? '—'} - {location.openTimes?.end ?? '—'}
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                <MapPin size={15} />
                Termin vor Ort
              </span>
            </div>
          </button>
        );
      })}
    </div>
  );
};
